import React, { useState } from "react";
import { Users, Send, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 text-sm outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--tint-mint)]";
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long" }) : "");

// Răspunsurile cititorilor la o întrebare deschisă „oricui de pe blog”.
export const QuestionReplies = ({ q, isOwner, onChange }) => {
  const [text, setText] = useState("");
  const [nickname, setNickname] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const replies = q.replies || [];

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 3) {
      toast.error("Scrie măcar câteva cuvinte.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/questions/${q.id}/replies`, { text: text.trim(), nickname: nickname.trim() });
      onChange({ ...q, replies: [...replies, data.reply] });
      setText("");
      setNickname("");
      setOpen(false);
      toast.success("Mulțumesc — răspunsul tău e în cutie.");
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut trimite răspunsul"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rid) => {
    try {
      await api.delete(`/admin/questions/${q.id}/replies/${rid}`);
      onChange({ ...q, replies: replies.filter((r) => r.id !== rid) });
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut șterge"));
    }
  };

  return (
    <div className="mt-6" data-testid={`qa-replies-${q.id}`}>
      <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#F7F1E8]/68">
        <Users className="h-3.5 w-3.5" strokeWidth={1.6} />
        {replies.length === 0 ? "Nimeni n-a răspuns încă" : `${replies.length} ${replies.length === 1 ? "răspuns" : "răspunsuri"} de la cititori`}
      </p>
      {replies.length > 0 && (
        <ul className="mt-3 space-y-2">
          {replies.map((r) => (
            <li key={r.id} data-testid={`qa-reply-${r.id}`} className="flex items-start justify-between gap-3 rounded-2xl bg-[var(--tint-butter)] px-4 py-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--sun-dark)]">{r.nickname || "cineva de pe blog"} · {fmt(r.created_at)}</p>
                <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-[#F7F1E8]/90">{r.text}</p>
              </div>
              {isOwner && (
                <button type="button" onClick={() => remove(r.id)} data-testid={`qa-reply-delete-${r.id}`} aria-label="Șterge răspunsul" className="shrink-0 rounded-full p-1.5 text-[#F7F1E8]/58 hover:bg-[#2B1B13] hover:text-[var(--accent-dark)]">
                  <Trash2 className="h-4 w-4" strokeWidth={1.6} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {open ? (
        <form onSubmit={submit} className="mt-3 space-y-2 rounded-2xl bg-[#2B1B13]/80 p-4 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
          <textarea data-testid={`qa-reply-text-${q.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={1500} placeholder="Ce i-ai spune tu? Cu blândețe." className={`${inputCls} leading-relaxed`} required />
          <input data-testid={`qa-reply-nick-${q.id}`} value={nickname} onChange={(e) => setNickname(e.target.value)} maxLength={40} placeholder="Cum să-ți spunem (opțional)" className={inputCls} />
          <div className="flex gap-2">
            <button type="submit" disabled={busy} data-testid={`qa-reply-submit-${q.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--sun)] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--milk)] disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" strokeWidth={1.8} />} Trimite răspunsul
            </button>
            <button type="button" onClick={() => setOpen(false)} className="rounded-full px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#F7F1E8]/68">Renunță</button>
          </div>
        </form>
      ) : (
        <button type="button" onClick={() => setOpen(true)} data-testid={`qa-reply-open-${q.id}`} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--sun-dark)_40%,transparent)] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--sun-dark)] hover:bg-[var(--tint-butter)]">
          Răspunde și tu
        </button>
      )}
    </div>
  );
};

export default QuestionReplies;
